/**
 * CreatorExchangeModule — the Creator Exchange tab (P10).
 *
 * The monetization surface: a tip jar, patronage + premium-tier subscriptions,
 * game-pass sales, world rental (incl. Reputation Collateral), Creator
 * Certification, and the DevEx-style analytics/settlement view. The client only
 * RENDERS the replicated rows and CALLS the reducers — every split (the 15% cut),
 * cap, royalty, and consequence is server-authoritative. The UI gates a few things
 * for UX (affordability, self-purchase, the cert badge) but the server is the
 * authority and rejects anything invalid.
 *
 * Data comes straight from the SDK's replicated cache (`conn.db.*`, subscribed by
 * AtlasNetworkModule). The module self-binds row callbacks that re-emit
 * `atlas:exchange-updated`, so any P10 change re-renders. There is no client-held
 * authoritative state — consistent with "the client mirrors, never authorizes".
 */

import type { App, IModule } from 'enginex';
import { Identity } from 'spacetimedb';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { sfx } from '../lib/sfx.js';

const TIER_LABEL: Record<string, string> = {
  free: 'Free',
  tier_premium: 'Premium',
  tier_creator: 'Creator',
  tier_sovereign: 'Sovereign World',
};

export class CreatorExchangeModule implements IModule {
  readonly name = 'CreatorExchange';

  private bound = false;
  private els: Record<string, HTMLElement> = {};
  /** hex → Identity, rebuilt each render so selects can resolve a target row. */
  private accounts = new Map<string, Identity>();

  init(_app: App): void {
    const ids = [
      'cx-tier', 'cx-tier-note',
      'cx-to', 'cx-tip-amount', 'cx-tip-btn', 'cx-tip-note',
      'cx-patron-amount', 'cx-patron-btn', 'cx-patron-note',
      'cx-pass-world', 'cx-pass-price', 'cx-pass-grants', 'cx-pass-create-btn', 'cx-pass-note', 'cx-pass-list',
      'cx-rent-world', 'cx-rent-upfront', 'cx-rent-collateral', 'cx-rent-btn', 'cx-rent-note', 'cx-rent-list',
      'cx-cert-status', 'cx-cert-btn',
      'cx-settle-btn', 'cx-analytics',
    ];
    for (const id of ids) {
      const el = document.getElementById(id);
      if (el) this.els[id] = el;
    }

    bus.on('atlas:account-ready', () => {
      this.bind();
      this.render();
    });
    bus.on('atlas:exchange-updated', () => this.render());
    bus.on('atlas:wallet-updated', () => this.render());
    bus.on('atlas:reputation-updated', () => this.render());

    this.wireButtons();
    this.render();
  }

  /** Register row callbacks once so any P10 change re-renders the tab. */
  private bind(): void {
    if (this.bound) return;
    const conn = atlas.conn;
    if (!conn) return;
    this.bound = true;
    const ping = () => bus.emit('atlas:exchange-updated', {});
    const tables = [
      conn.db.subscription, conn.db.gamePass, conn.db.gamePassOwner, conn.db.rental,
      conn.db.tipRecord, conn.db.royalty, conn.db.creatorCert, conn.db.entitlement,
      conn.db.dailySettlement,
    ];
    for (const tbl of tables) {
      // Every generated table exposes onInsert/onUpdate/onDelete.
      const t = tbl as unknown as {
        onInsert(cb: () => void): void;
        onUpdate(cb: () => void): void;
        onDelete(cb: () => void): void;
      };
      t.onInsert(ping);
      t.onUpdate(ping);
      t.onDelete(ping);
    }
  }

  private self(): Identity | null {
    const conn = atlas.conn;
    return conn ? (conn.identity ?? null) : null;
  }

  private wireButtons(): void {
    this.els['cx-tip-btn']?.addEventListener('click', () => this.tip());
    this.els['cx-patron-btn']?.addEventListener('click', () => this.patron());
    this.els['cx-pass-create-btn']?.addEventListener('click', () => this.createPass());
    this.els['cx-rent-btn']?.addEventListener('click', () => this.startRent());
    this.els['cx-cert-btn']?.addEventListener('click', () => this.requestCert());
    this.els['cx-settle-btn']?.addEventListener('click', () => this.settle());
    // Tier subscribe buttons are rendered with the tier panel.
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  private render(): void {
    const conn = atlas.conn;
    const self = this.self();
    if (!conn || !self) return;

    this.accounts.clear();
    const toSelect = this.els['cx-to'] as HTMLSelectElement | undefined;
    const opts: string[] = [];
    for (const a of conn.db.account.iter()) {
      const hex = a.identity.toHexString();
      this.accounts.set(hex, a.identity);
      if (hex !== atlas.identityHex) opts.push(`<option value="${hex}">${this.esc(a.displayName || a.username)} · ${hex.slice(0, 8)}…</option>`);
    }
    if (toSelect) {
      const prev = toSelect.value;
      toSelect.innerHTML = opts.join('') || '<option value="">(no other accounts)</option>';
      if (prev && this.accounts.has(prev)) toSelect.value = prev;
    }

    this.renderTier(conn, self);
    this.renderPasses(conn, self);
    this.renderRentals(conn, self);
    this.renderCert(conn, self);
    this.renderAnalytics(conn, self);
  }

  private renderTier(conn: NonNullable<typeof atlas.conn>, self: Identity): void {
    const ent = conn.db.entitlement.identity.find(self);
    const tier = ent ? ent.tier : 'free';
    const el = this.els['cx-tier'];
    if (!el) return;
    const quota = ent ? ent.worldQuota : 1;
    const cap = ent ? ent.priceCapSov.toString() : '100';
    const forge = ent ? ent.forgeAccess : false;
    const analytics = ent ? ent.analytics : false;
    el.innerHTML =
      `<div class="head"><span class="kind">Tier: ${TIER_LABEL[tier] ?? tier}</span></div>` +
      `<div class="meta">worlds ${quota} · Canvas price cap ${cap} SOV · Forge ${forge ? '✓' : '—'} · analytics ${analytics ? '✓' : '—'}</div>` +
      `<div class="actions">` +
      this.tierBtn('tier_premium', 'Premium (1000)') +
      this.tierBtn('tier_creator', 'Creator (3000)') +
      this.tierBtn('tier_sovereign', 'Sovereign (50000)') +
      `</div>`;
    for (const kind of ['tier_premium', 'tier_creator', 'tier_sovereign']) {
      el.querySelector(`[data-tier="${kind}"]`)?.addEventListener('click', () => this.subscribeTier(kind));
    }
  }

  private tierBtn(kind: string, label: string): string {
    return `<button data-tier="${kind}" class="action secondary">${label}</button>`;
  }

  private renderPasses(conn: NonNullable<typeof atlas.conn>, self: Identity): void {
    const list = this.els['cx-pass-list'];
    if (!list) return;
    const passes = [...conn.db.gamePass.iter()];
    if (passes.length === 0) {
      list.innerHTML = '<div class="empty">No game passes listed.</div>';
      return;
    }
    list.innerHTML = '';
    for (const p of passes) {
      const owned = [...conn.db.gamePassOwner.iter()].some((o) => o.passId === p.passId && o.owner.isEqual(self));
      const mine = p.creator.isEqual(self);
      const el = document.createElement('div');
      el.className = 'market-item';
      el.innerHTML =
        `<div class="head"><span class="kind">Pass #${p.passId} · world ${p.worldId}</span><span class="price">${p.priceSov} SOV</span></div>` +
        `<div class="meta">${mine ? 'your pass' : p.creator.toHexString().slice(0, 8) + '…'} · "${this.esc(p.grants)}"</div>`;
      const actions = document.createElement('div');
      actions.className = 'actions';
      const btn = document.createElement('button');
      if (mine) { btn.textContent = 'Your pass'; btn.disabled = true; }
      else if (owned) { btn.textContent = 'Owned'; btn.disabled = true; }
      else if (atlas.walletSov < p.priceSov) { btn.textContent = `Need ${p.priceSov} SOV`; btn.disabled = true; }
      else { btn.textContent = `Buy · ${p.priceSov} SOV`; btn.addEventListener('click', () => this.buyPass(p.passId)); }
      actions.appendChild(btn);
      el.appendChild(actions);
      list.appendChild(el);
    }
  }

  private renderRentals(conn: NonNullable<typeof atlas.conn>, self: Identity): void {
    const list = this.els['cx-rent-list'];
    if (!list) return;
    const rentals = [...conn.db.rental.iter()].filter((r) => r.owner.isEqual(self) || r.renter.isEqual(self));
    if (rentals.length === 0) {
      list.innerHTML = '<div class="empty">No rentals.</div>';
      return;
    }
    list.innerHTML = '';
    for (const r of rentals) {
      const iOwn = r.owner.isEqual(self);
      const el = document.createElement('div');
      el.className = 'market-item';
      const path = r.collateralVouch ? 'collateral' : 'cash';
      const status = r.active ? 'active' : (r.returnedClean ? 'returned clean' : 'returned trashed');
      el.innerHTML =
        `<div class="head"><span class="kind">Rental #${r.rentalId} · world ${r.worldId}</span><span class="price">${r.upfrontSov} SOV</span></div>` +
        `<div class="meta">${iOwn ? 'you own · renter ' + r.renter.toHexString().slice(0, 8) + '…' : 'you rent'} · ${path} · ${status}</div>`;
      if (iOwn && r.active) {
        const actions = document.createElement('div');
        actions.className = 'actions';
        const clean = document.createElement('button');
        clean.className = 'action';
        clean.textContent = 'End · clean';
        clean.addEventListener('click', () => this.endRent(r.rentalId, true));
        const trash = document.createElement('button');
        trash.className = 'action secondary';
        trash.textContent = 'End · trashed';
        trash.addEventListener('click', () => this.endRent(r.rentalId, false));
        actions.append(clean, trash);
        el.appendChild(actions);
      }
      list.appendChild(el);
    }
  }

  private renderCert(conn: NonNullable<typeof atlas.conn>, self: Identity): void {
    const el = this.els['cx-cert-status'];
    if (!el) return;
    const cert = conn.db.creatorCert.identity.find(self);
    const rep = conn.db.reputation.identity.find(self);
    const trust = rep ? rep.trustworthiness : 50;
    let reviews = 0;
    for (const rv of conn.db.review.iter()) if (rv.subject.isEqual(self)) reviews += 1;
    const certified = cert ? cert.certified : false;
    el.textContent = certified
      ? '✓ Certified — you may monetize Forge experiences.'
      : `Not certified. Bar: Trust ≥ 75 (you ${trust}) + ≥ 5 peer reviews (you ${reviews}).`;
    el.className = certified ? 'note ok' : 'note';
  }

  private renderAnalytics(conn: NonNullable<typeof atlas.conn>, self: Identity): void {
    const el = this.els['cx-analytics'];
    if (!el) return;
    const rows = [...conn.db.dailySettlement.iter()].filter((d) => d.creator.isEqual(self)).sort((a, b) => (a.forDay < b.forDay ? 1 : -1));
    if (rows.length === 0) {
      el.innerHTML = '<div class="empty">No settlements yet. Earn revenue, then run a daily settlement.</div>';
      return;
    }
    el.innerHTML = rows
      .map((d) => `<div class="market-item"><div class="head"><span class="kind">Day ${d.forDay}</span><span class="price">net ${d.netSov} SOV</span></div>` +
        `<div class="meta">gross ${d.grossSov} − 15% cut ${d.platformCutSov} = net ${d.netSov}</div></div>`)
      .join('');
  }

  // ─── Actions (every one is a server reducer call) ────────────────────────────

  private targetIdentity(): Identity | null {
    const sel = this.els['cx-to'] as HTMLSelectElement | undefined;
    const hex = sel?.value ?? '';
    return this.accounts.get(hex) ?? null;
  }

  private numVal(id: string): bigint {
    const el = this.els[id] as HTMLInputElement | undefined;
    const v = el ? Number(el.value) : 0;
    return Number.isFinite(v) && v > 0 ? BigInt(Math.floor(v)) : 0n;
  }

  private tip(): void {
    const conn = atlas.conn;
    const to = this.targetIdentity();
    const amount = this.numVal('cx-tip-amount');
    if (!conn || !to || amount <= 0n) return this.note('cx-tip-note', 'Pick a recipient and a positive amount.', 'err');
    this.note('cx-tip-note', 'Tipping…', '');
    void conn.reducers.tip({ to, worldId: atlas.currentWorldId ?? 0n, amount })
      .then(() => { sfx('sfx_coin_purchase', { gain: 0.35 }); this.note('cx-tip-note', `Tipped ${amount} SOV (creator gets 85%, platform 15%).`, 'ok'); })
      .catch((e: Error) => this.note('cx-tip-note', e.message, 'err'));
  }

  private patron(): void {
    const conn = atlas.conn;
    const to = this.targetIdentity();
    const amount = this.numVal('cx-patron-amount');
    if (!conn || !to || amount <= 0n) return this.note('cx-patron-note', 'Pick a creator and a positive amount.', 'err');
    this.note('cx-patron-note', 'Subscribing…', '');
    void conn.reducers.subscribe({ creator: to, kind: 'patronage', amount, periodDays: 30 })
      .then(() => this.note('cx-patron-note', `Patronage active — ${amount} SOV / 30d (first period charged).`, 'ok'))
      .catch((e: Error) => this.note('cx-patron-note', e.message, 'err'));
  }

  private subscribeTier(kind: string): void {
    const conn = atlas.conn;
    const self = this.self();
    if (!conn || !self) return;
    this.note('cx-tier-note', `Subscribing to ${TIER_LABEL[kind] ?? kind}…`, '');
    void conn.reducers.subscribe({ creator: self, kind, amount: 0n, periodDays: 30 })
      .then(() => this.note('cx-tier-note', `${TIER_LABEL[kind] ?? kind} tier active — entitlement granted.`, 'ok'))
      .catch((e: Error) => this.note('cx-tier-note', e.message, 'err'));
  }

  private createPass(): void {
    const conn = atlas.conn;
    if (!conn) return;
    const worldId = this.numVal('cx-pass-world');
    const price = this.numVal('cx-pass-price');
    const grants = (this.els['cx-pass-grants'] as HTMLInputElement | undefined)?.value ?? '';
    if (worldId <= 0n || price <= 0n) return this.note('cx-pass-note', 'Enter a world id and a positive price.', 'err');
    this.note('cx-pass-note', 'Listing pass…', '');
    void conn.reducers.createGamePass({ worldId, price, grants })
      .then(() => this.note('cx-pass-note', 'Game pass listed.', 'ok'))
      .catch((e: Error) => this.note('cx-pass-note', e.message, 'err'));
  }

  private buyPass(passId: bigint): void {
    const conn = atlas.conn;
    if (!conn) return;
    this.note('cx-pass-note', `Buying pass #${passId}…`, '');
    void conn.reducers.buyGamePass({ passId })
      .then(() => this.note('cx-pass-note', `Bought pass #${passId}.`, 'ok'))
      .catch((e: Error) => this.note('cx-pass-note', e.message, 'err'));
  }

  private startRent(): void {
    const conn = atlas.conn;
    const self = this.self();
    if (!conn || !self) return;
    const worldId = this.numVal('cx-rent-world');
    const upfront = this.numVal('cx-rent-upfront');
    const collateral = (this.els['cx-rent-collateral'] as HTMLInputElement | undefined)?.checked ?? false;
    if (worldId <= 0n || upfront <= 0n) return this.note('cx-rent-note', 'Enter a world id and a positive upfront.', 'err');
    this.note('cx-rent-note', 'Renting…', '');
    void conn.reducers.startRental({ worldId, renter: self, upfront, collateralVouch: collateral })
      .then(() => this.note('cx-rent-note', collateral ? 'Collateral rental started (half upfront, vouch-backed).' : 'Cash rental started.', 'ok'))
      .catch((e: Error) => this.note('cx-rent-note', e.message, 'err'));
  }

  private endRent(rentalId: bigint, returnedClean: boolean): void {
    const conn = atlas.conn;
    if (!conn) return;
    this.note('cx-rent-note', 'Ending rental…', '');
    void conn.reducers.endRental({ rentalId, returnedClean })
      .then(() => this.note('cx-rent-note', returnedClean ? 'Returned clean — renter Trustworthiness boosted.' : 'Marked trashed — collateral penalties applied.', 'ok'))
      .catch((e: Error) => this.note('cx-rent-note', e.message, 'err'));
  }

  private requestCert(): void {
    const conn = atlas.conn;
    if (!conn) return;
    void conn.reducers.requestCreatorCert({})
      .then(() => this.render())
      .catch(() => this.render());
  }

  private settle(): void {
    const conn = atlas.conn;
    if (!conn) return;
    void conn.reducers.settleDaily({}).then(() => this.render()).catch(() => this.render());
  }

  private note(id: string, text: string, cls: '' | 'ok' | 'err'): void {
    const el = this.els[id];
    if (!el) return;
    el.className = cls ? `note ${cls}` : 'note';
    el.textContent = text;
  }

  /** Escape user-controlled strings before interpolating into innerHTML (anti-XSS). */
  private esc(s: string): string {
    return s.replace(/[&<>"']/g, (c) =>
      c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
    );
  }
}
